import _ from 'underscore';

import { AuthorizableType } from './AuthorizableType';
import {
  CapabilityInfo,
  ICapabilityInfo
} from './Capabilities';
import { PermissionRoleName } from './PermissionRole';

export interface CapabilitySection {
  name: PermissionRoleName;
  capabilities: ReadonlyArray<ICapabilityInfo>;
}

const CollectionSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
];

const DatabaseSections = (showChangeHierarchy?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  {
    name: PermissionRoleName.Administer, capabilities: showChangeHierarchy ?
      [CapabilityInfo.changeHierarchy, CapabilityInfo.changePermissions] : [CapabilityInfo.changePermissions]
  },
];

const DatasourceSections = (isOnlineEnvironment?: boolean, allowNonAdminRefreshExtracts?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.connect] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.exportXmlDatasource] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write, CapabilityInfo.saveAsDatasource, CapabilityInfo.vizqlDataApiAccess, ...isOnlineEnvironment ? [CapabilityInfo.definePulseMetric, CapabilityInfo.certifyPulseMetric] : []] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions, ...allowNonAdminRefreshExtracts ? [CapabilityInfo.extractRefresh] : []] },
];

const FlowSections = (editingFlowsEnabled?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.exportXmlFlow] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.executeFlow, CapabilityInfo.write, ...editingFlowsEnabled ? [CapabilityInfo.webAuthoringForFlows] : []] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const PublishedConnectionsSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.connect] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.editPublishedConnection] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const ProjectSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.writeProject] },
];

const TableSections = (showChangeHierarchy?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  {
    name: PermissionRoleName.Administer, capabilities: showChangeHierarchy ?
      [CapabilityInfo.changeHierarchy, CapabilityInfo.changePermissions] : [CapabilityInfo.changePermissions]
  },
];

const ViewSections = (
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => {
  const showAiAccess =
    analyticsAssistantDashboardQAEnabled &&
    einsteinCopilotForTableauEnabled;
  return [
    { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.filter, CapabilityInfo.viewComments, CapabilityInfo.addComment, CapabilityInfo.exportImage, CapabilityInfo.exportData] },
    { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.shareView, CapabilityInfo.vud, CapabilityInfo.webAuthoring, CapabilityInfo.connectWorkbook, ...showAiAccess ? [CapabilityInfo.aiAccess] : []] },
    { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.delete, CapabilityInfo.changePermissions, CapabilityInfo.connectWorkbook, ...showAiAccess ? [CapabilityInfo.aiAccess] : []] },
  ];
};

const WorkbookSections = (
  explainDataPermissionEnabled?: boolean,
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => {
  const showAiAccess =
    analyticsAssistantDashboardQAEnabled &&
    einsteinCopilotForTableauEnabled;
  return [
    { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.filter, CapabilityInfo.viewComments, CapabilityInfo.addComment, CapabilityInfo.exportImage, CapabilityInfo.exportData] },
    { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.shareView, CapabilityInfo.vud, CapabilityInfo.webAuthoring, ...explainDataPermissionEnabled ? [CapabilityInfo.explainData] : [], CapabilityInfo.connectWorkbook, ...showAiAccess ? [CapabilityInfo.aiAccess] : []] },
    { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.exportXmlWorkbook, CapabilityInfo.write, CapabilityInfo.vizqlDataApiAccess, CapabilityInfo.connectWorkbook, ...showAiAccess ? [CapabilityInfo.aiAccess] : []] },
    { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions, ...allowNonAdminRefreshExtracts ? [CapabilityInfo.extractRefresh] : [], CapabilityInfo.connectWorkbook, ...showAiAccess ? [CapabilityInfo.aiAccess] : []] },
  ];
};

// NOTE: This mapping gets initialized in PermissionsDialog. To add a new capability behind a FF,
// the FF should be passed in as a parameter so it can be used to build the desired capability sections.
export const CapabilitySectionsByAuthorizableType = (
  editingFlowsEnabled?: boolean,
  explainDataPermissionEnabled?: boolean,
  databasesInProjectsPostMVPEnabled?: boolean,
  isOnlineEnvironment?: boolean,
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
): _.Dictionary<ReadonlyArray<CapabilitySection>> => {
  return {
    [AuthorizableType.COLLECTION]: CollectionSections,
    [AuthorizableType.DATABASE]: DatabaseSections(databasesInProjectsPostMVPEnabled),
    [AuthorizableType.DATABASE_TABLE_TEMPLATE]: TableSections(databasesInProjectsPostMVPEnabled),
    [AuthorizableType.DATASOURCE]: DatasourceSections(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.FLOW]: FlowSections(editingFlowsEnabled),
    [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionsSections,
    [AuthorizableType.PROJECT]: ProjectSections,
    [AuthorizableType.PROJECT_DATABASE_TEMPLATE]: DatabaseSections(databasesInProjectsPostMVPEnabled),
    [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceSections(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowSections(editingFlowsEnabled),
    [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionsSections,
    [AuthorizableType.PROJECT_TABLE_TEMPLATE]: TableSections(databasesInProjectsPostMVPEnabled),
    [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookSections(
      explainDataPermissionEnabled,
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.TABLE]: TableSections(databasesInProjectsPostMVPEnabled),
    [AuthorizableType.VIEW]: ViewSections(
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.WORKBOOK]: WorkbookSections(
      explainDataPermissionEnabled,
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
  };
};
